import React, { useState } from 'react';
import { LogOut, Menu, X } from 'lucide-react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { navigationForRole } from '../../config/navigation';
import { clearUserData, getBrotherData } from '../../utils/auth';

const Frame = styled.div`
  min-height: 100vh;
  padding-left: 248px;
  @media (max-width: 800px) { padding-left: 0; }
`;
const Sidebar = styled.aside`
  background: ${({ theme }) => theme.colors.surface};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  bottom: 0; display: flex; flex-direction: column; left: 0; padding: 18px 12px;
  position: fixed; top: 0; width: 248px; z-index: 30;
  @media (max-width: 800px) {
    box-shadow: 12px 0 30px rgba(20,20,18,.12);
    transform: translateX(${({ $open }) => ($open ? '0' : '-105%')});
    transition: transform .2s ease;
  }
`;
const Brand = styled.div`
  align-items: center; display: flex; font-family: ${({ theme }) => theme.typography.display};
  font-size: 21px; gap: 10px; padding: 7px 9px 24px;
  &::before { background: ${({ theme }) => theme.colors.accent}; border-radius: 50%; content: ''; height: 10px; width: 10px; }
`;
const Nav = styled.nav`display: flex; flex: 1; flex-direction: column; gap: 3px;`;
const NavItem = styled(NavLink)`
  align-items: center; border-radius: ${({ theme }) => theme.radius.sm}; color: ${({ theme }) => theme.colors.textMuted};
  display: flex; font-weight: 500; gap: 10px; padding: 9px 10px; text-decoration: none;
  &:hover { background: ${({ theme }) => theme.colors.surfaceMuted}; color: ${({ theme }) => theme.colors.text}; }
  &.active { background: ${({ theme }) => theme.colors.accentSoft}; color: ${({ theme }) => theme.colors.accent}; }
`;
const Account = styled.div`border-top: 1px solid ${({ theme }) => theme.colors.border}; padding: 14px 8px 0;`;
const Name = styled.div`font-weight: 650;`;
const Role = styled.div`color: ${({ theme }) => theme.colors.textMuted}; font-size: 12px; margin-top: 2px;`;
const Logout = styled.button`
  align-items: center; background: none; border: 0; color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer; display: flex; gap: 8px; margin-top: 12px; padding: 5px 0;
`;
const Topbar = styled.header`
  align-items: center; background: rgba(247,247,244,.92); border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex; height: 58px; justify-content: space-between; padding: 0 32px; position: sticky; top: 0; z-index: 20;
  backdrop-filter: blur(10px);
  @media (max-width: 800px) { padding: 0 16px; }
`;
const TopTitle = styled.div`font-size: 13px; font-weight: 650;`;
const MenuButton = styled.button`
  background: none; border: 0; cursor: pointer; display: none; padding: 5px;
  @media (max-width: 800px) { display: grid; place-items: center; }
`;
const CloseButton = styled(MenuButton)`align-self: flex-end; display: none; @media (max-width: 800px) { display: grid; position: absolute; right: 12px; top: 14px; }`;
const Content = styled.main`margin: 0 auto; max-width: 1440px; padding: 32px; @media (max-width: 800px) { padding: 22px 16px; }`;

export default function AppShell() {
  const brother = getBrotherData();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const items = navigationForRole(brother.position);
  const current = items.find((item) =>
    item.path.startsWith('/') ? location.pathname === item.path : location.pathname.endsWith(`/${item.path}`)
  );

  const logout = () => {
    clearUserData();
    navigate('/login', { replace: true });
  };

  return (
    <Frame>
      <Sidebar $open={open}>
        <CloseButton aria-label="Close navigation" onClick={() => setOpen(false)}><X size={20} /></CloseButton>
        <Brand>Rushtracker</Brand>
        <Nav aria-label="Dashboard navigation">
          {items.map(({ path, label, icon: Icon }) => (
            <NavItem key={path} to={path.startsWith('/') ? path : `/app/${path}`} onClick={() => setOpen(false)}>
              <Icon size={17} strokeWidth={1.8} />{label}
            </NavItem>
          ))}
        </Nav>
        <Account>
          <Name>{brother.name}</Name><Role>{brother.position}</Role>
          <Logout onClick={logout}><LogOut size={15} /> Log out</Logout>
        </Account>
      </Sidebar>
      <Topbar>
        <MenuButton aria-label="Open navigation" onClick={() => setOpen(true)}><Menu size={21} /></MenuButton>
        <TopTitle>{current?.label || 'Dashboard'}</TopTitle>
        <span />
      </Topbar>
      <Content><Outlet /></Content>
    </Frame>
  );
}
